import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import config from '@wearetla/tla-essentials-tools/config';

// Context
import { useAuth } from '@wearetla/tla-essentials-tools/utilities/auth';
import { createI18nInstance } from '/utilities/i18n';

// Services
import lotteryServices from '/services/lottery';

// QR adresi örneği:
// https://qr.ifsokak.com/siparis-sorgula/?CompID=GMQ9899354&BrID=IMG6383032&ModulNo=2&BelgeID=P740ee4c84a
const qrParamKeys = ['CompID', 'BrID', 'ModulNo', 'BelgeID'];

export const parseLotteryQrParams = (search) => {
	const searchParams = new URLSearchParams(search ?? '');

	const params = qrParamKeys.reduce((params, key) => {
		const value = searchParams.get(key);

		return value ? { ...params, [key]: value } : params;
	}, {});

	return Object.keys(params).length === qrParamKeys.length ? params : false;
};

export const parseLotteryQrUrl = (qrUrl) => {
	try {
		return parseLotteryQrParams(new URL(qrUrl).search);
	} catch (e) {
		return false;
	}
};

export const LotteryContext = createContext({
	enabled: false,
	initialized: false,
	lotteryCount: 0,
	userLotteryStatus: false,
	lotteries: [],
	branches: [],
	getLottery: () => {},
	submitOrder: () => {},
	submitQrUrl: () => {},
});

export const LotteryProvider = ({ children }) => {
	const { loggedIn } = useAuth();
	const enabled = !!config.lottery?.enabled;
	const { t } = createI18nInstance();

	const [data, setData] = useState(null);
	const [initialized, setInitialized] = useState(false);

	const getLottery = useCallback(() => {
		if (!enabled || !loggedIn) {
			setData(null);
			setInitialized(true);

			return Promise.resolve(null);
		}

		return new Promise((resolve, reject) => {
			lotteryServices
				.getLottery()
				.then((payload) => {
					setData(payload);
					resolve(payload);
				})
				.catch((feedback) => {
					setData(false);
					reject(feedback);
				})
				.finally(() => {
					setInitialized(true);
				});
		});
	}, [enabled, loggedIn]);

	useEffect(() => {
		setInitialized(false);

		getLottery().catch(() => {});
	}, [getLottery]);

	const submitOrder = useCallback(
		(params) => {
			if (!params) {
				return Promise.reject(t('Hatalı bir QR okuttun.'));
			}

			return new Promise((resolve, reject) => {
				lotteryServices
					.submitOrder(params)
					.then((feedback) => {
						getLottery()
							.catch(() => {})
							.finally(() => {
								resolve(feedback);
							});
					})
					.catch(reject);
			});
		},
		[getLottery, t]
	);

	const submitQrUrl = useCallback(
		(qrUrl) => {
			return submitOrder(parseLotteryQrUrl(qrUrl));
		},
		[submitOrder]
	);

	const api = useMemo(() => {
		const branchList = config.lottery?.branches ?? [];

		// Her hak, kazanıldığı şubenin bilgisiyle (config'de tanımlı değilse `null`).
		const lotteries = (data ? data.lotteries : []).map((item) => ({
			...item,
			branch: branchList.find((branch) => branch.id === item.client_branch_id) ?? null,
		}));

		// Her şubenin kendi çekilişi var: config'deki her şube için o şubeden gelen haklar ve toplamları.
		const branches = branchList.map((branch) => {
			const items = lotteries.filter((item) => item.client_branch_id === branch.id);

			return {
				...branch,
				items,
				count: items.reduce((total, item) => total + (item.lottery_count ?? 0), 0),
			};
		});

		return {
			enabled,
			initialized,
			lotteryCount: data ? data.lottery_count : 0,
			userLotteryStatus: !!data?.user_lottery_status,
			lotteries,
			branches,
			failed: data === false,
			getLottery,
			submitOrder,
			submitQrUrl,
		};
	}, [enabled, data, initialized, getLottery, submitOrder, submitQrUrl]);

	return <LotteryContext.Provider value={api}>{children}</LotteryContext.Provider>;
};
